import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import preferencesService from '../services/preferencesService';
import { changeLanguage, getCurrentLanguage } from '../i18n/i18n';

export const usePreferences = () => {
  const { t } = useTranslation();
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Charger les préférences au montage du composant
  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🌍 Chargement des préférences utilisateur...');
      const response = await preferencesService.getPreferences();
      
      if (response.success) {
        setPreferences(response.preferences);
        
        // Ne synchroniser la langue que si aucune langue n'est définie localement
        const currentLang = getCurrentLanguage();
        const localStoredLang = localStorage.getItem('nodemented-language');
        
        if (response.preferences.language && !localStoredLang) {
          console.log(`🌍 Synchronisation langue: undefined → ${response.preferences.language}`);
          changeLanguage(response.preferences.language);
        } else if (localStoredLang && localStoredLang !== response.preferences.language) {
          // Si l'utilisateur a une langue locale différente, mettre à jour la base
          console.log(`🔄 Mise à jour langue en base: ${response.preferences.language} → ${localStoredLang}`);
          await saveLanguage(localStoredLang);
        }
      }
    } catch (err) {
      console.error('Erreur chargement préférences:', err);
      setError(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async (newPreferences) => {
    try {
      setSaving(true);
      setError(null);
      
      const response = await preferencesService.savePreferences(newPreferences);
      
      if (response.success) {
        setPreferences(response.preferences);
        
        // Synchroniser la langue avec i18n si changée
        if (newPreferences.language && newPreferences.language !== getCurrentLanguage()) {
          console.log(`🔄 Langue changée: ${getCurrentLanguage()} → ${newPreferences.language}`);
          changeLanguage(newPreferences.language);
        }
        
        return { success: true, message: response.message };
      } else {
        throw new Error(response.error || 'Erreur de sauvegarde');
      }
    } catch (err) {
      console.error('Erreur sauvegarde préférences:', err);
      const errorMessage = err.response?.data?.error || err.message || t('errors.generic');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setSaving(false);
    }
  };

  const saveLanguage = async (language) => {
    try {
      setSaving(true);
      setError(null);
      
      // Éviter de sauvegarder si la langue est déjà la même
      if (getCurrentLanguage() === language) {
        console.log(`🌍 Langue déjà définie: ${language}`);
        return { success: true, message: 'Langue déjà définie' };
      }
      
      const response = await preferencesService.saveLanguagePreference(language);
      
      if (response.success) {
        setPreferences(prev => ({
          ...prev,
          language
        }));
        
        // La langue a déjà été changée dans LanguageSelector, pas besoin de rechanger ici
        console.log(`🌍 Langue sauvegardée en base: ${language}`);
        
        return { success: true, message: response.message };
      } else {
        throw new Error(response.error || 'Erreur de sauvegarde');
      }
    } catch (err) {
      console.error('Erreur sauvegarde langue:', err);
      const errorMessage = err.response?.data?.error || err.message || t('errors.generic');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setSaving(false);
    }
  };

  const saveTheme = async (theme) => {
    try {
      setSaving(true);
      setError(null);
      
      const response = await preferencesService.saveThemePreference(theme);
      
      if (response.success) {
        setPreferences(prev => ({
          ...prev,
          theme
        }));
        
        return { success: true, message: response.message };
      } else {
        throw new Error(response.error || 'Erreur de sauvegarde');
      }
    } catch (err) {
      console.error('Erreur sauvegarde thème:', err);
      const errorMessage = err.response?.data?.error || err.message || t('errors.generic');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setSaving(false);
    }
  };

  const saveNotifications = async (notifications) => {
    try {
      setSaving(true);
      setError(null);
      
      const response = await preferencesService.saveNotificationPreferences(notifications);
      
      if (response.success) {
        setPreferences(prev => ({
          ...prev,
          notifications
        }));
        
        return { success: true, message: response.message };
      } else {
        throw new Error(response.error || 'Erreur de sauvegarde');
      }
    } catch (err) {
      console.error('Erreur sauvegarde notifications:', err);
      const errorMessage = err.response?.data?.error || err.message || t('errors.generic');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setSaving(false);
    }
  };

  const resetPreferences = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const response = await preferencesService.resetPreferences();
      
      if (response.success) {
        setPreferences(response.preferences);
        
        // Remettre la langue par défaut
        changeLanguage(response.preferences.language);
        
        return { success: true, message: response.message };
      } else {
        throw new Error(response.error || 'Erreur de réinitialisation');
      }
    } catch (err) {
      console.error('Erreur réinitialisation préférences:', err);
      const errorMessage = err.response?.data?.error || err.message || t('errors.generic');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setSaving(false);
    }
  };

  return {
    preferences,
    loading,
    saving,
    error,
    loadPreferences,
    savePreferences,
    saveLanguage,
    saveTheme,
    saveNotifications,
    resetPreferences,
    clearError: () => setError(null)
  };
};

export default usePreferences;
